import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TWS_API_URL } from "../environment/apis.config";
import { getAccessToken } from "../utils/auth.util";
import { useCustomUnitsContext } from "./CustomUnitsContext";

const DEFAULT_UNITS = {
  temperature: "F",
  wind_speed: "KTS",
  precipitationRate: "MM/HR",
  ceiling: "ft",
  visibility: "miles",
  pressure: "inHg",
};

const fetchUserProfile = async () => {
const token = await getAccessToken();
  const response = await fetch(
    `${TWS_API_URL}/customUserProfile`, {
        headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return response.json();
};


const updateUserUnits = async (units: typeof DEFAULT_UNITS) => {
  const customProfile = {
    timeRepresentation: 'localtime-region/city',
    unitOutput: 'concatenated',
    units,
  }
  const token = await getAccessToken();
  const response = await fetch(`${TWS_API_URL}/customUserProfile`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ customProfile }),
  });

  if (!response.ok) throw new Error(`Update failed with status ${response.status}`);
  return response.json();
};


const useTruWeatherUnits = () => {
  const queryClient = useQueryClient();
  const { setCustomUnits } = useCustomUnitsContext();

  const query = useQuery({
    queryKey: ["truweather-user-profile"],
    queryFn: fetchUserProfile,
    select: (data) => {
      const units = data.customProfile.units;
      return {
        temperature: units.temperature || DEFAULT_UNITS.temperature,
        wind_speed: units.wind_speed || DEFAULT_UNITS.wind_speed,
        precipitationRate: units.precipitationRate || DEFAULT_UNITS.precipitationRate,
        ceiling: units.ceiling || DEFAULT_UNITS.ceiling,
        visibility: units.visibility || DEFAULT_UNITS.visibility,
        pressure: units.pressure || DEFAULT_UNITS.pressure,
      };
    },
  });

  const mutation = useMutation({
    mutationFn: updateUserUnits,
    onSuccess: (_, updatedUnits) => {
      setCustomUnits(updatedUnits);
      queryClient.invalidateQueries({ queryKey: ["truweather-user-profile"] });
    },
  });

  return {
    ...query,
    updateUnits: mutation.mutate,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
};

export default useTruWeatherUnits;